import { StyleSheet, View } from 'react-native';

import { useEstimatedPointsEarned } from '@fhs/loyalty/src/queries/loyalty.queries';
import { Text } from '@fhs/ui';
import { TempCartPriceMismatchLogger } from '@fhs-legacy/frontend/src/state/cart/temp-cart-price-mismatch-logger';
import { CART_SUBTOTAL } from '@fhs-legacy/frontend/src/utils/test-ids';

import { useCartPriceOrder } from '../../../hooks/use-cart-price-order';
import { useOffersLoadingWithTrailingSettle } from '../../../hooks/use-offers-loading-with-trailing-settle';
import { EstimatedPointsEarned } from '../../estimated-points-earned';
import { OriginalPriceDisplay } from '../item/item-profile/item-price/item-price-components';

type CartSubtotalsProps = {
  baseTotal: number;
  grandTotal: number;
};

export function CartSubtotals({ baseTotal = 0, grandTotal = 0 }: CartSubtotalsProps) {
  const { serverOrder, incentiveApplied } = useCartPriceOrder();
  const isOffersEvaluating = useOffersLoadingWithTrailingSettle();
  const { data: estimatedPoints, isPending: isLoadingPoints, enabled: isPointsEnabled } = useEstimatedPointsEarned();
  const displayedSubtotal = isOffersEvaluating ? baseTotal : grandTotal;
  const formattedSubtotal = displayedSubtotal < 0 ? '0.00' : (displayedSubtotal / 100).toFixed(2);
  const shouldShowDiscount = !isOffersEvaluating && incentiveApplied && baseTotal !== grandTotal;

  return (
    <View style={styles.container}>
      <View
        style={styles.subtotalContainer}
        accessibilityRole="text"
        accessibilityLabel={`Subtotal: ${formattedSubtotal}${shouldShowDiscount ? '. Discount applied' : ''}`}
      >
        <Text.Heading type="four">Subtotal</Text.Heading>
        <View style={styles.priceContainer}>
          {shouldShowDiscount && (
            <OriginalPriceDisplay subtotalID price={baseTotal} shouldShowStrikeThrough />
          )}
          <Text.Heading type="four" testID={CART_SUBTOTAL}>
            ${formattedSubtotal}
          </Text.Heading>
        </View>
      </View>
      {isPointsEnabled && (isLoadingPoints || estimatedPoints) && (
        <EstimatedPointsEarned
          totalPointsEarned={estimatedPoints?.totalPointsEarned}
          isLoading={isLoadingPoints}
        />
      )}
      <View
        style={styles.taxesAndFeesContainer}
        accessibilityRole="text"
        accessibilityLabel="Taxes and fees will be calculated at the final step"
      >
        <Text.Paragraph size="sm">Taxes and Fees</Text.Paragraph>
        <Text.Paragraph size="sm">Calculated at Final Step</Text.Paragraph>
      </View>
      <TempCartPriceMismatchLogger serverOrder={serverOrder} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 32,
  },
  subtotalContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 16,
    paddingBottom: 6,
  },
  priceContainer: {
    flexDirection: 'row',
    gap: 9,
    alignItems: 'center',
  },
  taxesAndFeesContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 12,
    paddingTop: 8
  },
});
